/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import 'lazysizes';
import { gsap } from 'gsap';
import { debounce } from 'lodash-es';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import Vue from 'vue';
import Headroom from 'headroom.js';
import zenScroll from 'zenscroll';
import { ScrollTrigger } from './gsap-bonus/ScrollTrigger';

require('./bootstrap');

require('./components/accordion-component');
require('./components/contact-form-component');
require('./components/downloads-filter-component');
require('./components/filter-component');
require('./components/highlight-component');
require('./components/home-slider-component');
require('./components/panel-slider-component');
require('./components/slider-component');
require('./components/video-component');
require('./components/product-header-buttons');

gsap.registerPlugin(ScrollTrigger);

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            headroom: null,
            headroomEl: null,
            showNavigation: false,
        };
    },

    watch: {
        isMobile(value) {
            EventBus.$emit('is-mobile-changed');
        },

        isTablet(value) {
            EventBus.$emit('is-tablet-changed');
        },

        showNavigation(visible) {
            EventBus.$emit('navigation-state-changed', visible);

            if (this.showNavigation) {
                this.openNavigation();

                return;
            }

            this.closeNavigation();
        },
    },

    mounted() {
        zenScroll.setup(null, 0);
        this.headroomEl = document.querySelector('.header-holder');
        this.headroom = new Headroom(this.headroomEl);
        this.headroom.init();

        this.initRevealTriggers();

        window.addEventListener('resize', this.onWindowResize);

        this.appHeight();

        EventBus.$on('window-resize', () => {
            this.appHeight();
        });
    },

    methods: {
        appHeight() {
            const doc = document.documentElement;
            doc.style.setProperty('--header-height', `${window.innerHeight}px`);
        },

        openNavigation() {
            disableBodyScroll(document.querySelector('#navigation__menu-holder'), { reserveScrollBarGap: true });

            document.body.classList.remove('body-lock');

            const menuWidth = document.querySelector('.navigation__menu').offsetWidth;
            const menuHeight = document.querySelector('.navigation__menu').offsetHeight;
            const photosWidth = document.querySelector('.navigation__image-holder').offsetWidth;
            const photosHeight = document.querySelector('.navigation__image-holder').offsetHeight;

            this.navigationTl = gsap.timeline({ repeat: 0 });

            this.navigationTl.set('.navigation__menu-holder-bg-left', { width: menuWidth, height: menuHeight }, 0);
            this.navigationTl.set('.navigation__menu-holder-bg-right', { width: photosWidth, height: photosHeight }, 0);
            this.navigationTl.set('.navigation__menu-list', {
                x: 0,
                autoAlpha: 1,
            }, 0);

            this.navigationTl.to('.navigation__menu-bar', { duration: 0.6, scaleX: 1, ease: 'Power3.inOut' }, 0);
            this.navigationTl.to('.navigation__menu-holder-bg', {
                duration: 0.6, scaleX: '1', ease: 'Power3.inOut',
            }, 0.1);

            this.navigationTl.to('.navigation__image-right__picture-holder', {
                duration: 0.4, x: 0, scale: 1, ease: 'Power3.inOut',
            }, 0.4);
            this.navigationTl.to('.navigation__image-left__picture-holder', {
                duration: 0.4, x: 0, scale: 1, ease: 'Power3.inOut',
            }, 0.45);
            this.navigationTl.to('.navigation__slogan', {
                duration: 0.6, autoAlpha: 1, ease: 'Power3.inOut',
            }, 0.6);
            this.navigationTl.to('.navigation__menu-item-animate', {
                duration: 0.8,
                autoAlpha: 1,
                y: 0,
                ease: 'power3.out',
                stagger: 0.08,
            }, 0.6);
        },

        closeNavigation() {
            document.body.classList.add('body-lock');
            enableBodyScroll(document.querySelector('#navigation__menu-holder'));

            this.navigationTl.reverse();
            this.navigationTl.duration(1);
        },

        initRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('[data-reveal]'),
                ...document.querySelectorAll('[data-fade-reveal]'),
                ...document.querySelectorAll('[data-image-reveal]'),
            ];

            elements.forEach((element) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === '') {
                    return;
                }

                // if (revealPosition) {
                //     console.log(revealPosition);
                // }

                const startPercentage = revealPosition || '90%';

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        if (!revealDelay) {
                            element.classList.add('--revealed');
                            return;
                        }

                        gsap.delayedCall(revealDelay, () => {
                            element.classList.add('--revealed');
                        });
                    },
                });
            });
        },
        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(() => {
            EventBus.$emit('window-resize');
        }, 150),

        detectMobile() {
            this.isMobile = window.innerWidth <= 768;
            this.isTablet = window.innerWidth <= 1024;
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    },
});
